import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookHeart, Loader2, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { BookletOrderList } from './BookletOrderList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerseRow = ({ verse, checked, disabled, onToggle }) => (
  <label
    className={`flex items-start gap-3 p-3 rounded-lg border border-[#D4AF37]/15 bg-[#160A0D] transition-colors ${disabled ? 'opacity-50' : 'cursor-pointer hover:border-[#D4AF37]/40'}`}
    data-testid={`booklet-verse-${verse.id}`}
  >
    <Checkbox checked={checked} disabled={disabled} onCheckedChange={onToggle} className="mt-1" data-testid={`booklet-verse-checkbox-${verse.id}`} />
    <div className="min-w-0">
      <p className="text-sm text-[#F4EFE6] font-serif italic">{verse.preview_text}</p>
      <p className="text-xs text-[#C2B49A] font-cinzel mt-1">{verse.name} · {verse.source}</p>
    </div>
  </label>
);

export const DevotionalBookletDialog = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [verses, setVerses] = useState([]);
  const [max, setMax] = useState(8);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState('');
  const [dedication, setDedication] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleOpen = async (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/scripture-pdf/booklet/sources`, { headers });
      setVerses(res.data.verses);
      setMax(res.data.max_verses);
    } catch (e) {
      toast.error('Could not load your verses');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/scripture-pdf/booklet`,
        { font_ids: selected, title, dedication },
        { headers, responseType: 'blob' }
      );
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'Devotional_Booklet.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Your Devotional Booklet is ready');
      setOpen(false);
      setSelected([]);
    } catch (e) {
      toast.error('Could not create the booklet. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary rounded-lg font-cinzel tracking-wider" data-testid="open-booklet-dialog-btn">
          <BookHeart className="w-4 h-4 mr-2" />
          Create Devotional Booklet
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 bg-[#0D0507] border border-[#D4AF37]/30 text-[#F4EFE6]" data-testid="booklet-dialog">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-[#F4EFE6]">Devotional Booklet</DialogTitle>
          <DialogDescription className="text-[#C2B49A] font-serif">
            Choose up to {max} verses from your fonts and collections. Each gets its own page with the poster, meaning and significance.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Booklet title (e.g. Psalms for Mum)" className="bg-[#160A0D] border-[#D4AF37]/20" data-testid="booklet-title-input" />
          <Input value={dedication} onChange={(e) => setDedication(e.target.value)} maxLength={200} placeholder="Dedication (optional)" className="bg-[#160A0D] border-[#D4AF37]/20" data-testid="booklet-dedication-input" />
        </div>
        <div className="max-h-[32vh] overflow-y-auto space-y-2 pr-1" data-testid="booklet-verse-list">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>
          ) : verses.length === 0 ? (
            <p className="text-sm text-[#C2B49A] font-serif text-center py-10" data-testid="booklet-empty">
              No verses with Scripture Insight yet. Generate a font first, then come back.
            </p>
          ) : (
            verses.map((v) => (
              <VerseRow key={v.id} verse={v} checked={selected.includes(v.id)}
                disabled={!selected.includes(v.id) && selected.length >= max} onToggle={() => toggle(v.id)} />
            ))
          )}
        </div>
        <BookletOrderList ids={selected} verses={verses} onChange={setSelected} />
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-[#C2B49A] font-serif" data-testid="booklet-selected-count">{selected.length} / {max} selected</p>
          <Button onClick={create} disabled={creating || selected.length === 0} className="btn-primary rounded-lg" data-testid="create-booklet-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {creating ? 'Binding your booklet...' : 'Create PDF'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
